import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {noop} from 'lodash';
import {EDSApplication} from '@ellucian/react-design-system/core';
import {createEDSTheme} from '@ellucian/react-design-system/core/themes';
import {
  colorCtaBlueBase,
  colorCtaBlueHover,
  colorCtaBlueActive,
  colorCtaBlueTint,
  colorCtaGreenActive,
  colorCtaGreenBase,
  colorCtaGreenHover,
  colorCtaGreenTint,
  colorCtaIrisActive,
  colorCtaIrisBase,
  colorCtaIrisHover,
  colorCtaIrisTint,
  colorCtaPlumActive,
  colorCtaPlumBase,
  colorCtaPlumHover,
  colorCtaPlumTint,
  colorCtaTaupeActive,
  colorCtaTaupeBase,
  colorCtaTaupeHover,
  colorCtaTaupeTint,
} from '@ellucian/react-design-system/core/styles/tokens';
import {useDispatch} from 'react-redux';

import useFeatureFlags from 'src/hooks/useFeatureFlags';
import {
  getFeatureInteraction,
  postFeatureInteraction,
  patchFeatureInteraction,
} from 'shared/actions/feature-interactions';
import {flashMessage} from 'shared/actions/notifications';

import './path-design-context.scss';

const baseTheme = createEDSTheme('light');
const ctaColors = {
  blue: {
    active: colorCtaBlueActive,
    base: colorCtaBlueBase,
    hover: colorCtaBlueHover,
    tint: colorCtaBlueTint,
  },
  green: {
    active: colorCtaGreenActive,
    base: colorCtaGreenBase,
    hover: colorCtaGreenHover,
    tint: colorCtaGreenTint,
  },
  iris: {
    active: colorCtaIrisActive,
    base: colorCtaIrisBase,
    hover: colorCtaIrisHover,
    tint: colorCtaIrisTint,
  },
  plum: {
    active: colorCtaPlumActive,
    base: colorCtaPlumBase,
    hover: colorCtaPlumHover,
    tint: colorCtaPlumTint,
  },
  taupe: {
    active: colorCtaTaupeActive,
    base: colorCtaTaupeBase,
    hover: colorCtaTaupeHover,
    tint: colorCtaTaupeTint,
  },
};

export type EllucianCtaColors = 'blue' | 'green' | 'iris' | 'plum' | 'taupe';

interface PathDesign {
  enabled: boolean;
  theme: EllucianCtaColors;
  changeTheme: (theme: EllucianCtaColors) => void;
}

export const PathDesignContext = createContext<PathDesign>({
  enabled: false,
  theme: 'blue',
  changeTheme: noop,
});

const PathDesignProvider = ({children}: PropsWithChildren) => {
  const dispatch = useDispatch();
  const flags = useFeatureFlags(['path_design']);
  const [theme, setTheme] = useState<EllucianCtaColors>('blue');

  const changeTheme = useCallback(
    (newTheme: EllucianCtaColors) => {
      setTheme(newTheme);
      patchFeatureInteraction('path-design-theme', {cta_color: newTheme}).then(() => {
        dispatch(flashMessage('CTA color saved successfully!', 'success'));
      });
    },
    [dispatch]
  );

  const value = useMemo(
    () => ({enabled: flags.pathDesign, theme, changeTheme}),
    [flags.pathDesign, theme, changeTheme]
  );

  const ellucianTheme = useMemo(
    () => ({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        ctaColor: {...baseTheme.palette.ctaColor, ...ctaColors[theme]},
      },
    }),
    [theme]
  );

  useEffect(() => {
    if (!flags.pathDesign) return;

    const themeClassName = `--${theme}`;
    document.documentElement.classList.add(themeClassName);

    return () => {
      document.documentElement.classList.remove(themeClassName);
    };
  }, [flags.pathDesign, theme]);

  useEffect(() => {
    getFeatureInteraction('path-design-theme', ['ctaColor'])
      .then(({ctaColor}) => {
        setTheme(ctaColor);
      })
      .catch(() => {
        postFeatureInteraction('path-design-theme', {cta_color: 'blue'});
      });
  }, []);

  return (
    <PathDesignContext.Provider value={value}>
      {flags.pathDesign ? (
        <EDSApplication theme={ellucianTheme}>
          <div className="shared-path-design">{children}</div>
        </EDSApplication>
      ) : (
        children
      )}
    </PathDesignContext.Provider>
  );
};

export default PathDesignProvider;
